<template>
  <v-card elevation="3" class="my-5 card-fixed-height">
    <v-card-title class="card-header py-3 px-4">
      <v-icon color="catViolet" class="mr-2 flex-shrink-0">
        {{ icon || "mdi-book-open-variant" }}
      </v-icon>
      <span class="card-title-text">{{ infoTitle }}</span>
      <v-chip
        v-if="hasData"
        x-small
        color="catViolet"
        outlined
        class="ml-2 flex-shrink-0"
      >
        {{ notes.length }}
      </v-chip>
      <!-- salvia-781/878: atalho de inserção, sempre visível. Abre o diálogo
           direto no formulário; o botão ao lado abre o histórico. -->
      <v-tooltip v-if="showQuickAdd" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1 card-quick-add"
            :aria-label="`Adicionar em ${infoTitle}`"
            @click="openQuickAdd"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Adicionar sem abrir o histórico</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1"
            @click="openDialog"
          >
            <v-icon data-tour="pd-card-menu">mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <span>Ver notas e editar</span>
      </v-tooltip>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pa-4 card-content">
      <!-- Estado vazio -->
      <div v-if="!hasData" class="text-center py-6">
        <v-icon size="48" color="grey lighten-1">mdi-book-open-outline</v-icon>
        <p class="text-caption grey--text mt-2 mb-0">
          Nenhuma nota registrada ainda.
        </p>
        <v-btn
          small
          color="catViolet"
          outlined
          class="mt-3"
          @click="openQuickAdd"
        >
          <v-icon small left>mdi-plus</v-icon>
          Adicionar nota
        </v-btn>
      </div>

      <!-- Notas recentes -->
      <div v-else class="life-history-timeline">
        <div
          v-for="entry in recentEntries"
          :key="entry.id"
          class="history-entry"
        >
          <div class="entry-date">
            <v-icon x-small class="mr-1" color="catViolet">mdi-calendar</v-icon>
            {{ formatDate(entry.created_at) }}
            <v-chip
              x-small
              label
              class="ml-2 kind-chip"
              :color="kindColor(entry.kind)"
              text-color="white"
            >
              {{ kindLabel(entry.kind) }}
            </v-chip>
          </div>
          <div class="entry-content">
            {{ entry.content }}
          </div>
        </div>

        <div v-if="notes.length > 3" class="text-center mt-2">
          <v-chip small outlined color="catViolet" @click="openDialog">
            <v-icon left small>mdi-plus</v-icon>
            {{ notes.length - 3 }} mais
            {{ notes.length - 3 === 1 ? "nota" : "notas" }}
          </v-chip>
        </div>
      </div>
    </v-card-text>

    <EditClinicalNotesDialog
      v-model="dialogVisible"
      :open-direct-add="quickAddMode"
      :dialog-title="infoTitle"
      :notes="notes"
      :patient-id="patientId"
      @data-saved="handleDataSaved"
      @data-deleted="handleDataChanged"
    />
  </v-card>
</template>

<script>
import { getNotesByPatient } from "@/services/clinicalNoteService";
import EditClinicalNotesDialog from "@/features/patients/profile-dashboard/dialogs/EditClinicalNotesDialog.vue";

import patientDataRefresh from "@/features/patients/profile-dashboard/mixins/patientDataRefresh";

import dashboardStore from "@/features/patients/profile-dashboard/patientDashboardStore";
import { formatDate } from "@/utils/datetime";
import cardQuickAdd from "@/features/patients/profile-dashboard/mixins/cardQuickAdd.js";
import cardItemCount from "@/features/patients/profile-dashboard/mixins/cardItemCount.js";

export default {
  mixins: [patientDataRefresh, cardQuickAdd, cardItemCount],
  name: "LifeHistoryCard",

  components: {
    EditClinicalNotesDialog,
  },

  props: {
    // O título vem de config/dashboardCards.js, via a página do dashboard.
    // Sem default de propósito: duplicar o texto aqui já fez a renomeação
    // do card passar despercebida uma vez.
    infoTitle: { type: String, required: true },
    patientId: { type: [String, Number], required: true },
    icon: { type: String, default: null },
  },

  data() {
    return {
      dialogVisible: false,
      notes: [],
    };
  },

  computed: {
    /** SALVIA-938: itens do card, para o contador da caixa lateral. */
    itemCount() {
      return (this.notes || []).length;
    },
    hasData() {
      return this.notes.length > 0;
    },
    recentEntries() {
      return this.notes.slice(0, 3);
    },
  },

  watch: {
    patientId: "fetchData",
  },

  created() {
    this.fetchData().finally(() => this.$emit("card-loaded"));
  },

  methods: {
    formatDate,
    async fetchData() {
      if (!this.patientId) return;
      try {
        this.notes = await dashboardStore.section(this.patientId, "clinical_notes", () => getNotesByPatient(this.patientId));
      } catch (error) {
        console.error("Erro ao buscar notas clínicas:", error);
        this.notes = [];
      }
    },

    kindLabel(kind) {
      return (
        { life_history: "Histórico", general: "Geral", other: "Outro" }[kind] ||
        "Nota"
      );
    },

    kindColor(kind) {
      return (
        { life_history: "#6D28D9", general: "#0891B2", other: "#64748B" }[kind] ||
        "#64748B"
      );
    },

    openDialog() {
      this.dialogVisible = true;
    },

    async handleDataChanged() {
      await this.fetchData();
      this.$emit("data-updated");
    },
  },
};
</script>

<style scoped>
/* ═══ Tema: Roxo / Lavanda (Histórico de Vida) ═══ */
.card-fixed-height {
  display: flex;
  flex-direction: column;
  border-radius: 14px !important;
  border: 1px solid #DDD6FE !important;
  box-shadow: 0 2px 10px rgba(124, 58, 237, 0.10) !important;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.card-fixed-height:hover {
  border-color: #C4B5FD !important;
  box-shadow: 0 6px 20px rgba(124, 58, 237, 0.18) !important;
}

/* ═══ Header com gradient sutil ═══ */
.card-header {
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  min-width: 0;
  background: linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%);
  border-bottom: 1px solid #DDD6FE;
}

.card-title-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--ds-purple);
  letter-spacing: 0.3px;
}

/* Os dois cards de largura cheia (Historico de Vida e Insights) NAO usam a
   altura fixa de 320px dos demais; eles reproduzem o tamanho antigo do
   Historico de Vida, um pouco mais baixo: cabecalho + 200px de corpo.
   `height` e nao `max-height` de proposito — com max-height o card encolhia
   quando havia pouco conteudo, e os dois, que ficam um embaixo do outro em
   largura cheia, apareciam com alturas diferentes. */
.card-content {
  height: 200px;
  overflow-y: auto;
}

.card-content::-webkit-scrollbar {
  width: 6px;
}
.card-content::-webkit-scrollbar-thumb {
  background: #C4B5FD;
  border-radius: 10px;
}
.card-content::-webkit-scrollbar-thumb:hover {
  background: var(--ds-purple);
}

/* ═══ Timeline de entradas ═══ */
.life-history-timeline {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.history-entry {
  padding: 10px 12px;
  border-radius: 8px;
  background-color: #F5F3FF;
  border-left: 3px solid #7C3AED;
  transition: background-color 0.15s ease, transform 0.15s ease;
}

.history-entry:hover {
  background-color: #EDE9FE;
  transform: translateX(2px);
}

.entry-date {
  font-size: 10px;
  font-weight: 700;
  color: var(--ds-purple);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
}

.kind-chip {
  height: 16px !important;
  font-size: 9px !important;
  font-weight: 700 !important;
}

.entry-content {
  font-size: 13px;
  line-height: 1.5;
  color: var(--ds-text-strong);
  font-weight: 500;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
